import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { 
  Users, 
  Eye, 
  Calendar,
  ArrowLeft,
  AlertCircle,
  Brain,
  TrendingUp,
  Target,
  Compass,
  FileText,
  Clock
} from 'lucide-react';

const ParentAssessmentResults = () => {
  const navigate = useNavigate();
  const { familyMembers } = useFamilyContext();
  const [selectedChild, setSelectedChild] = useState(null);
  const [sharedAssessments, setSharedAssessments] = useState([]);
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get children with college access
  const children = useMemo(() => {
    return familyMembers.filter(member => member.role === 'child');
  }, [familyMembers]);

  // Set first child as default selection
  useEffect(() => {
    if (children.length > 0 && !selectedChild) {
      setSelectedChild(children[0]);
    }
  }, [children, selectedChild]);

  // Load shared assessments for selected child
  useEffect(() => {
    if (selectedChild) {
      loadSharedAssessments();
    }
  }, [selectedChild]);

  const loadSharedAssessments = async () => {
    if (!selectedChild) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get possible user IDs for the selected child
      const possibleUserIds = [
        selectedChild.auth_user_id,
        selectedChild.authUserId,
        selectedChild.user_id,
        selectedChild.id
      ].filter(Boolean);

      console.log('🔍 Loading shared assessments for child:', selectedChild.name, possibleUserIds);

      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessment_responses')
        .select('*')
        .in('user_id', possibleUserIds)
        .eq('parent_can_view', true)
        .order('completed_at', { ascending: false });

      if (assessmentError) {
        console.error('Error loading shared assessments:', assessmentError);
        setError('Failed to load assessment results');
        return;
      }

      console.log('✅ Found shared assessments:', assessmentData);
      setSharedAssessments(assessmentData || []);

      // Load the most recent insights
      if (assessmentData && assessmentData.length > 0) {
        setInsights(assessmentData[0].insights);
      } else {
        setInsights(null);
      }

    } catch (error) {
      console.error('Error loading shared assessments:', error);
      setError('Failed to load assessment results');
    } finally {
      setIsLoading(false);
    }
  };

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-stone-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">No Children Found</h2>
          <p className="text-stone-600">No children with college access found in your family.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/college/parent-hub')}
                className="text-stone-500 hover:text-stone-700 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Assessment Results</h1>
                <p className="text-stone-600">View shared assessment results from your children</p>
              </div>
            </div>
            
            {/* Child Selector */}
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-stone-500" />
              <select
                value={selectedChild?.id || ''}
                onChange={(e) => {
                  const child = children.find(c => c.id === e.target.value);
                  setSelectedChild(child);
                }}
                className="border border-stone-300 rounded-lg px-3 py-2 text-stone-900 bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name || child.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-stone-600">Loading assessment results...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">Error Loading Results</h2>
            <p className="text-stone-600 mb-4">{error}</p>
            <button
              onClick={loadSharedAssessments}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : sharedAssessments.length === 0 ? (
          <div className="text-center py-12">
            <Eye className="w-16 h-16 text-stone-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">No Shared Results</h2>
            <p className="text-stone-600 mb-4">
              {selectedChild?.name || 'This child'} hasn't shared their assessment results with you yet.
            </p>
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-azure-800 text-sm">
                <strong>Tip:</strong> Students can share their results by clicking the "Share with Parent" button 
                on their assessment results page.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Assessment Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Eye className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Shared Results</h3>
                    <p className="text-sm text-stone-600">{sharedAssessments.length} assessment(s)</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-azure-100 p-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-azure-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Last Shared</h3>
                    <p className="text-sm text-stone-600">
                      {sharedAssessments[0]?.shared_at 
                        ? new Date(sharedAssessments[0].shared_at).toLocaleDateString()
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-violet-100 p-2 rounded-lg">
                    <Brain className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Assessment Type</h3>
                    <p className="text-sm text-stone-600">
                      {sharedAssessments[0]?.assessment_type === 'enhanced' ? 'Strategic Profile' : 'Conversational'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment Results */}
            {insights && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-3 rounded-full">
                    <Brain className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-stone-900">
                      {selectedChild?.name || 'Student'}'s Strategic Profile
                    </h2>
                    <p className="text-stone-600">Shared assessment results</p>
                  </div>
                </div>

                {/* Strategic Advantage */}
                {insights.strategicAdvantage && (
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200 rounded-2xl p-6 mb-8">
                    <div className="text-sm font-bold text-yellow-700 uppercase tracking-wide mb-3">
                      Strategic Advantage
                    </div>
                    <div className="text-lg font-semibold text-stone-900 leading-relaxed">
                      {insights.strategicAdvantage}
                    </div>
                  </div>
                )}

                {/* Insight Cards Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Intellectual Signature */}
                  {insights.intellectualSignature && (
                    <ParentInsightCard
                      type="signature"
                      icon={<Brain className="w-6 h-6" />}
                      title={insights.intellectualSignature.title}
                      subtitle={insights.intellectualSignature.subtitle}
                      content={insights.intellectualSignature.content}
                      details={insights.intellectualSignature.leveragePoints}
                    />
                  )}

                  {/* Hidden Superpowers */}
                  {insights.hiddenSuperpowers && (
                    <ParentInsightCard
                      type="superpowers"
                      icon={<TrendingUp className="w-6 h-6" />}
                      title={insights.hiddenSuperpowers.title}
                      subtitle={insights.hiddenSuperpowers.subtitle}
                      content={insights.hiddenSuperpowers.content}
                      details={insights.hiddenSuperpowers.superpowers}
                    />
                  )}

                  {/* Strategic Positioning */}
                  {insights.strategicPositioning && (
                    <ParentInsightCard
                      type="positioning"
                      icon={<Target className="w-6 h-6" />}
                      title={insights.strategicPositioning.title}
                      subtitle={insights.strategicPositioning.subtitle}
                      content={insights.strategicPositioning.whyItWorks}
                      details={insights.strategicPositioning.deployment}
                    />
                  )}

                  {/* Essay Strategy */}
                  {insights.essayStrategy && (
                    <ParentInsightCard
                      type="essays"
                      icon={<FileText className="w-6 h-6" />}
                      title={insights.essayStrategy.title}
                      subtitle={insights.essayStrategy.subtitle}
                      content={insights.essayStrategy.theme}
                      details={insights.essayStrategy.essayTopics}
                    />
                  )}
                </div>

                {/* College Recommendations */}
                {insights.collegeRecommendations && (
                  <div className="bg-stone-50 rounded-2xl p-6 mb-6">
                    <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                      <Compass className="w-5 h-5" />
                      College Recommendations
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {insights.collegeRecommendations.reach && (
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-red-700 mb-2">🚀 Reach Schools</h4>
                          <ul className="space-y-1 text-sm text-stone-700">
                            {insights.collegeRecommendations.reach.map((school, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-red-500 mt-1">•</span>
                                <span>{school}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {insights.collegeRecommendations.target && (
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-700 mb-2">🎯 Target Schools</h4>
                          <ul className="space-y-1 text-sm text-stone-700">
                            {insights.collegeRecommendations.target.map((school, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-yellow-500 mt-1">•</span>
                                <span>{school}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {insights.collegeRecommendations.safety && (
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-emerald-700 mb-2">✅ Safety Schools</h4>
                          <ul className="space-y-1 text-sm text-stone-700">
                            {insights.collegeRecommendations.safety.map((school, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-emerald-500 mt-1">•</span>
                                <span>{school}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Plan */}
                {insights.actionPlan && (
                  <div className="bg-stone-50 rounded-2xl p-6">
                    <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Action Plan
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {insights.actionPlan.map((action, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {action.step}
                            </div>
                            <h4 className="font-medium text-stone-900">{action.title}</h4>
                          </div>
                          <p className="text-sm text-stone-600 mb-2">{action.description}</p>
                          <p className="text-xs text-teal-600 font-medium">⏱️ {action.timeline}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable insight card component for parent view
const ParentInsightCard = ({ type, icon, title, subtitle, content, details }) => {
  const cardStyles = {
    signature: 'border-teal-200 bg-teal-50',
    superpowers: 'border-pink-200 bg-pink-50',
    positioning: 'border-yellow-200 bg-yellow-50',
    essays: 'border-violet-200 bg-violet-50'
  };

  return (
    <div className={`border-2 ${cardStyles[type]} rounded-2xl p-6`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="text-stone-700">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-bold text-stone-900">{title}</h3>
          <p className="text-sm text-stone-600">{subtitle}</p>
        </div>
      </div>
      
      {content && (
        <p className="text-stone-700 mb-4 leading-relaxed">{content}</p>
      )}
      
      {details && (
        <ul className="space-y-2">
          {details.map((detail, idx) => (
            <li key={idx} className="text-sm text-stone-600 flex items-start gap-2">
              <span className="text-stone-400 mt-1">•</span>
              <span>{detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ParentAssessmentResults;